/**
 * Delete Asset Modal.
 *
 * Confirms an asset deletion and exposes the detach-vs-delete-activities
 * choice when the asset has linked activities. Ported from the deprecated
 * `EmissionsAssetDetailPage` so the new dashboard keeps the same safety
 * net (defaulting to "keep activities, just unlink").
 *
 * On success the parent owns navigation (e.g. push back to the list).
 */

import { useEffect, useState } from 'react';
import { Button as ReactstrapButton } from 'reactstrap';
import { ConfirmationModal } from '@g17eco/molecules/confirm-modal';
import { toastFromError } from '@g17eco/molecules/toasts';
import { useDeleteAssetMutation } from '../../api/emissions';
import { type AssetDeleteMode, type EmissionAsset } from '../../types/emissions';

interface DeleteAssetModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Called after the delete API resolves so the parent can navigate / toast. */
  onDeleted: () => void;
  initiativeId: string;
  asset: EmissionAsset;
}

export const DeleteAssetModal = ({
  isOpen,
  onClose,
  onDeleted,
  initiativeId,
  asset,
}: DeleteAssetModalProps) => {
  const [mode, setMode] = useState<AssetDeleteMode>('detach_activities');
  const [deleteAsset, { isLoading: isDeleting }] = useDeleteAssetMutation();

  // Always reset to the safe default when the modal reopens — a previous
  // session shouldn't leave "delete all linked activities" pre-selected.
  useEffect(() => {
    if (isOpen) setMode('detach_activities');
  }, [isOpen]);

  const handleConfirm = async () => {
    try {
      await deleteAsset({ initiativeId, assetId: asset._id, mode }).unwrap();
      onDeleted();
    } catch (error) {
      toastFromError(error);
    }
  };

  return (
    <ConfirmationModal
      title='Delete Asset'
      isOpen={isOpen}
      handleCancel={onClose}
      content={
        <div>
          <p>
            Are you sure you want to delete <strong>{asset.name}</strong>?
            {asset.activityCount > 0 && (
              <span> This asset has {asset.activityCount} linked activit{asset.activityCount === 1 ? 'y' : 'ies'}.</span>
            )}
          </p>
          {asset.activityCount > 0 && (
            <div className='tw:mt-4 tw:space-y-2'>
              <label className='tw:flex tw:items-center tw:gap-2 tw:cursor-pointer'>
                <input
                  type='radio'
                  name='deleteMode'
                  checked={mode === 'detach_activities'}
                  onChange={() => setMode('detach_activities')}
                />
                <span>Keep activities (unlink from asset)</span>
              </label>
              <label className='tw:flex tw:items-center tw:gap-2 tw:cursor-pointer'>
                <input
                  type='radio'
                  name='deleteMode'
                  checked={mode === 'delete_activities'}
                  onChange={() => setMode('delete_activities')}
                />
                <span className='tw:text-destructive'>Delete all linked activities</span>
              </label>
            </div>
          )}
        </div>
      }
      confirmButton={
        <ReactstrapButton color='danger' onClick={handleConfirm} disabled={isDeleting}>
          {isDeleting ? 'Deleting...' : 'Delete Asset'}
        </ReactstrapButton>
      }
    />
  );
};
